// Mirrors middleware's `src/tournament/interfaces/limited-link.interface.ts`. When a shape
// changes there, change it here in the same story.

export interface AgentInfo {
  agentId: string;
  // Admins see every agent's figures and may issue open links; agents see only their own.
  admin: boolean;
  trackingEnabled: boolean;
}

export interface IssueTournament {
  id: string;
  title: string;
  endAt: string;
}

export interface IssueOptions {
  tournaments: IssueTournament[];
  agent: { agentId: string; admin: boolean };
  locales: string[];
  defaultLocale: string;
}

export interface IssueLinkBody {
  tournamentId: string;
  // Left out, the link is open: any logged-in player can join with it. Admins only.
  playerUuid?: string;
  locale: string;
}

export interface IssuedLink {
  url: string;
  expiresAt: string;
}

export interface CohortSummary {
  linksIssued: number;
  playersInvited: number;
  playersRedeemed: number;
  redeemRate: number | null;
}

export type EventName = 'issued' | 'redeemed';

export interface EventItem {
  occurredAt: string;
  event: EventName;
  tournamentId: string;
  // For a join through an open link, the agent it is credited to.
  agentId: string;
  // null on the issue of an open link.
  playerUuid: string | null;
  linkId: string;
  alreadyJoined: boolean | null;
}

export interface OverviewTournament extends CohortSummary {
  tournamentId: string;
  agentIds: string[];
  firstIssuedAt: string;
  lastEventAt: string;
  title: string;
  status: string | null;
  startAt: string | null;
  endAt: string | null;
}

export interface OverviewAgent extends CohortSummary {
  agentId: string;
  lastEventAt: string;
}

export interface Overview {
  agent: { agentId: string; admin: boolean };
  totals: CohortSummary;
  tournaments: OverviewTournament[];
  agents: OverviewAgent[];
  recent: EventItem[];
  eventsCount: number;
}

export type WindowName = 'before' | 'during' | 'after7' | 'after30';

export interface WindowMetrics {
  activePlayers: number;
  retentionRate: number | null;
  depositors: number;
  depositCount: number;
  depositEur: number;
  ggrEur: number;
  ngrEur: number;
}

export interface AgentResults extends CohortSummary {
  agentId: string | null;
  retention: Record<WindowName, WindowMetrics>;
}

export interface WindowsInfo {
  before: { days: number; partial: false };
  during: { to: string; partial: boolean };
  after7: { from: string; to: string; partial: boolean; daysAvailable: number };
  after30: { from: string; to: string; partial: boolean; daysAvailable: number };
}

export interface TournamentResults {
  ok: true;
  tournament: { id: string; startAt: string; endAt: string };
  windows: WindowsInfo;
  totals: AgentResults;
  agents: AgentResults[];
  meta: { bytesProcessed: number; estCostUsd: number; cached: boolean; eventsCount: number };
}

export interface PlayerProfile {
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  country: string | null;
  currency: string | null;
  registrationDate: string | null;
  status: string | null;
}

export interface PlayerWindowMetrics {
  active: boolean;
  deposited: boolean;
  depositCount: number;
  depositEur: number;
  ggrEur: number;
  ngrEur: number;
}

export interface PlayerRow {
  playerUuid: string;
  agentIds: string[];
  linksIssued: number;
  locale: string | null;
  // null when nobody invited the player: they joined through an open link.
  invitedAt: string | null;
  redeemedAt: string | null;
  joinedViaOpenLink: boolean;
  profile: PlayerProfile | null;
  // ext_profile's `is_test`. True is why `profile` is null: test accounts are left out
  // of the player data bigquery-service mirrors, while their activity is still counted.
  isTest: boolean;
  lastActiveDay: string | null;
  windows: Record<WindowName, PlayerWindowMetrics> | null;
}

export interface TournamentPlayers {
  ok: true;
  tournament: { id: string; startAt: string; endAt: string };
  windows: WindowsInfo;
  players: PlayerRow[];
  meta: { bytesProcessed: number; estCostUsd: number; cached: boolean; eventsCount: number };
}

// ---- daily wheel spin history (HIS-484) — mirrors middleware's DailyWheelAdminSpin ----------

export type WheelTier = 'bronze' | 'silver' | 'gold';
export const WHEEL_TIERS: readonly WheelTier[] = ['bronze', 'silver', 'gold'];

// `drawn` is a spin whose grant never settled (middleware crashed between draw and credit);
// `declined` is a win the player passed on to keep their current bonus.
export type WheelSpinStatus = 'drawn' | 'pending_choice' | 'granted' | 'failed' | 'declined';
export const WHEEL_SPIN_STATUSES: readonly WheelSpinStatus[] = [
  'granted',
  'failed',
  'pending_choice',
  'declined',
  'drawn',
];

export interface WheelSpin {
  id: number;
  playerUuid: string;
  // UTC calendar day the spin counted for.
  day: string;
  // Tier the deposit entitled the player to.
  tier: WheelTier;
  // Wheel actually spun (a missing tier wheel falls down a tier).
  wheelUid: string;
  wheelTier: WheelTier;
  segmentUid: string;
  segmentLabel: string | null;
  campaignUuid: string | null;
  status: WheelSpinStatus;
  replacedCampaignUuid: string | null;
  depositTotal: number | null;
  depositCurrency: string | null;
  spunAt: string;
  settledAt: string | null;
}

export interface WheelSpinsPage {
  items: WheelSpin[];
  nextCursor: string | null;
}

export interface WheelSpinsFilters {
  playerUuid?: string;
  tier?: WheelTier | '';
  status?: WheelSpinStatus | '';
  from?: string;
  to?: string;
}

// ---- whales (HIS-507) — mirrors middleware's whales.interface.ts --------------------------

// top: the players with the most deposits in the range. tier1: everyone meeting the Tier 1
// rule over the latest three synced UTC days.
export type WhalesCohort = 'top' | 'tier1';

export interface WhalesFilters {
  cohort: WhalesCohort;
  // UTC days; empty leaves the default (the latest three synced days, "last 72 hours") to the API.
  from: string;
  to: string;
  limit: number;
}

export interface WhaleProfile {
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  currency: string | null;
  status: string | null;
}

export interface WhaleRow {
  playerUuid: string;
  // null only means the players mirror has not caught up: test accounts never reach this list.
  profile: WhaleProfile | null;
  country: string | null;
  // In the range, EUR. NGR is the analysts' mart figure.
  depositCount: number;
  depositEur: number;
  withdrawalEur: number;
  betEur: number;
  ggrEur: number;
  ngrEur: number;
  rewardEur: number;
  cashbackEur: number;
  bonusConvertedEur: number;
  lastActiveDay: string | null;
  // The game type with the most real-money bets in the range, and its share of them.
  mainGame: { gameType: string; betShare: number } | null;
  // The latest CRM message, segment inclusion or reward over activity.comms.
  lastComm: { channel: string; name: string | null; at: string } | null;
  // CRM messages per contact channel over activity.comms.
  channels: WhaleChannel[];
}

export interface WhaleChannel {
  channel: string;
  sent: number;
  // Followed by a completed real-money deposit within 24 hours.
  deposited24h: number;
}

export interface WhalesTier1Rule {
  days: number;
  minDeposits: number;
  minDepositEur: number;
  latamMinDepositEur: number;
  latamCountries: string[];
}

export interface DayRange {
  from: string;
  to: string;
}

export interface WhalesReport {
  ok: true;
  cohort: WhalesCohort;
  from: string | null;
  to: string | null;
  limit: number;
  // UTC days the hourly mirror holds; null until its first sync.
  coverage: DayRange | null;
  recentDays: DayRange | null;
  tier1Rule: WhalesTier1Rule;
  // UTC days the game and CRM mirrors hold; null until their first sync.
  activity: { games: DayRange | null; comms: DayRange | null };
  meta: { bytesProcessed: number; estCostUsd: number; cached: boolean; source?: string; lastSyncAt?: string | null };
  rows: WhaleRow[];
}

// Traffic quality: the affiliate / country / outlier report built from the analysts' tq tables. The
// report's own script reads the rows, so only `meta` is typed field by field here.
export interface TrafficQualityMeta {
  data_through: string;
  generated: string;
  source: string;
  grain: string;
  ret_threshold: number;
  fence: { dep_fence_eur: number; players: number; method: string; outliers: number };
}

export interface TrafficQualityData {
  meta: TrafficQualityMeta;
  groups: Record<string, unknown>[];
  regs: Record<string, unknown>[];
  outliers: Record<string, unknown>[];
}

// Mirrors middleware's traffic-quality.interface.ts (which mirrors bigquery-service's
// TrafficQualityResponse). `report` is null until bigquery-service's first sync.
export interface TrafficQualityResponse {
  ok: true;
  dataThrough: string | null;
  generatedAt: string | null;
  meta: { bytesProcessed: number; estCostUsd: number; cached: boolean; source?: string; lastSyncAt?: string | null };
  report: TrafficQualityData | null;
}
